// Uses KERMIT/YModem CRC16
import crc from "crc";
import { Buffer } from "buffer";

enum Direction {
  MACHINE = 0x02,
  APP = 0x01,
}

enum Product {
  STUDIO = 0x07,
  APP = 0x01,
}

export enum Command {
  CMD_CALIBRATE_GRIDER = 0x0dae, //DATA=1000
  CMD_FW_UPDATE = 0x1fa5, // Followed by ymodem upload
  CMD_MTU = 0x1da4,

  CMD_SET_WEIGHT_UNIT = 0x1f45, // ml = 0, g = 1, oz = 2
  CMD_SET_TEMP_UNIT = 0x1f4a, // C = 0, F = 1
  CMD_SET_WATER_SOURCE = 0x119c, // 0 = tank, 1 = direct
  CMD_SET_DISPLAY_BRIGHTNESS = 0x1fa7, // 0x01, 0x08, 0x0f

  CMD_INFO_BYPASS = 0x1fa6,

  CMD_SET_BREWER = 0x1f14,
  CMD_INFO_CAPSULE = 0x1fa8,

  CMD_SEND_RECIPE = 0x1f41, // One byte data (index?)
  CMD_SEND_RECIPE_WITHOUT_GRINDER = 0x1f44, // // One byte data

  CMD_START_RECIPE = 0x1f42, // // NO PAYLOAD

  CMD_PAUSE = 0x9e46,
  CMD_RESUME = 0x9e4c,
  CMD_RESTART = 0x9e47,

  CMD_DISPLAY_ENTER_SCALE = 0x1f43,
  CMD_DISPLAY_ENTER_GRIND = 0x1f46, // Two values
  CMD_DISPLAY_ENTER_WATER = 0x1f47, // Two values

  CND_DISPLAY_EXIT_SCALE = 0x1f4e,
  CMD_DISPLAY_EXIT_GRIND = 0x1f4c,
  CMD_DISPLAY_EXIT_BREW = 0x1f4d,

  CMD_DISPLAY_START_BREW_2 = 0x1f55,

  INFO_ERROR_RATIO = 0x200c,

  INFO_DISPLAY = 0x1f57, // index into enum of display screens
  INFO_SCALE = 0x5015, // Only one with 0x50 type, one float

  INFO_CURRENT_POD = 0x9e35, // Contains some bytes string

  INFO_GRINGING_ADJUSTING = 0x9e39,
  INFO_GRINIDING_START = 0x9e3a,
  INFO_GRINDING_END = 0x9e3b,

  INFO_BREWING_START = 0x9e3c,
  INFO_BLOOMING_START = 0x9e3e, // TODO contains 2 bytes data
  INFO_BREWING_END = 0x9e3f,

  INFO_FINISHED_BREW = 0x9e40,
  INFO_FINISHED_BREW2 = 0x9e41,

  INFO_BYPASS = 0x9e48,
  INFO_MACHINE = 0x9e49,

  INFO_WATER_LOW = 0x9e4a, // Contains 2 bytes data
  INFO_WATER_BREWING = 0x9e4b, // TODO contains Float data
  INFO_CURRENT_GRINDER = 0x9e4e, // TODO prove!
  INFO_STATE_CHANGE = 0x9e57,

  INFO_WATER_STATE = 0x2329,
  INFO_WEIGHT_TARE = 0x232f,
  INFO_WEIGHT_START = 0x232a,
  INFO_WEIGHT_END = 0x2330,

  // CMD AND INFO?
  STATE_MACHINE_ACTIVITY = 0x1f57, // TODO prove!
  STATE_MACHINE_SLEEPING = 0x1f49, // TODO prove!
  STATE_MACHINE_WAKEUP = 0x1f4b, // TODO prove!
}

export const ScreenNames: Record<number, string> = {
  [0x00]: "OFF?",
  [0x01]: "Home",
  [0x02]: "GrindSettings",
  [0x03]: "WaterSettings",
  [0x04]: "ScaleCalibration",
  [0x05]: "Scale",
  [0x06]: "GrindSize",
  [0x07]: "GrindSpeed",
  [0x08]: "Style",
  [0x09]: "Temperature",
  [0x0a]: "Heart",
  [0x1d]: "Paired",
  [0x1f]: "BrewConfig",
  [0x22]: "Grinding",
  [0x23]: "Brewing?",
};

export interface Weight {
  unknownWeight: number;
  weight: number;
  unknownNegative: number;
}

export interface GrindAdjustment {
  currentBurrs: number;
}

export interface BrewData {
  unknown_currentFlow: number;
  unknown_currentTemp: number;
}

interface Message {
  ID: number; // Should always be 0x58 'X'
  direction: Direction;
  product: Product;
  command: Command;
  total_length: number;
  type: number;
  payload: DataView;
  info_weight?: number;
  info_machine?: number;
  info_grind_adjust?: number;
  info_brewing?: number;
  info_ack?: "recipe" | "bypass" | "capsule" | "start";

  checksum1: number;
  checksum2: number;
}

export const parseMessage = (bytes: DataView) => {
  if (bytes.byteLength < 12) {
    console.log(
      "Invalid message length. Message is: ",
      Buffer.from(bytes.buffer).toString("hex"),
    );
    return;
  }
  const length = bytes.getUint8(5);
  const msg: Message = {
    ID: bytes.getUint8(0),
    direction: bytes.getUint8(1),
    product: bytes.getUint8(2),
    command: bytes.getUint16(3, true),
    total_length: length,
    type: bytes.getFloat32(6, true),
    payload: new DataView(bytes.buffer.slice(10, length - 2)),
    checksum1: bytes.getUint8(bytes.byteLength - 2),
    checksum2: bytes.getUint8(bytes.byteLength - 1),
  };
  switch (msg.command) {
    case Command.INFO_SCALE:
      msg.info_weight = msg.payload.getFloat32(0, true);
      // console.log("Weight:", msg.info_weight.toFixed(1));
      break;
    case Command.INFO_MACHINE:
      msg.info_machine = msg.payload.getUint32(0, true);
      console.log("Machine:", msg.info_machine);
      break;
    case Command.INFO_GRINGING_ADJUSTING:
      msg.info_grind_adjust = msg.payload.getFloat32(0);
      break;
    case Command.INFO_DISPLAY:
      const display = msg.payload.getUint32(0, true);
      console.log(
        "Display:",
        ScreenNames[display] || "0x" + display.toString(16),
      );
      break;
    case Command.CMD_INFO_BYPASS:
      console.log("Ack Bypass");
      msg.info_ack = "bypass";
      break;
    case Command.CMD_INFO_CAPSULE:
      console.log("Ack Capsule");
      msg.info_ack = "capsule";
      break;
    case Command.CMD_SEND_RECIPE:
      console.log("Ack Recipe");
      msg.info_ack = "recipe";
      break;
    case Command.CMD_START_RECIPE:
      console.log("Ack Start Recipe");
      msg.info_ack = "start";
      break;
    case Command.INFO_ERROR_RATIO:
      const error = msg.payload.getUint32(0, true);
      console.log("Error Ratio:", error);
      break;

    case Command.INFO_WATER_BREWING:
      msg.info_brewing = msg.payload.getFloat32(0);
      break;
    case Command.INFO_WATER_STATE:
      const amount = msg.payload.getUint32(0, true);
      const temperature = msg.payload.getUint32(4, true);
      const pattern = msg.payload.getUint32(8, true);
      console.log("Water State:", amount, temperature, pattern);
      break;
    case Command.INFO_WEIGHT_TARE:
    case Command.INFO_WEIGHT_START:
    case Command.INFO_WEIGHT_END:
      console.log("Weight Tare/Start/End");
      break;

    default:
      console.log(
        "Unknown Command",
        "0x" + msg.command.toString(16),
        `length=${msg.total_length - 12}(${msg.total_length})`,
        `type=${msg.type}`,
        Buffer.from(msg.payload.buffer).toString("hex"),
      );
  }
  return msg;
};

export const calculateChecksum = (fullMessage: DataView) => {
  const msg = new Uint8Array(
    fullMessage.buffer.slice(0, fullMessage.byteLength - 2),
  );
  console.log(
    Array.from(msg)
      .map((byte) => byte.toString(16))
      .join(" "),
  );

  const crc16 = crc.crc16kermit(msg, 0);
  return crc16;
};

export const printMessage = (msg: Message) => {
  if (
    msg.command in
    [
      Command.INFO_SCALE,
      Command.INFO_GRINGING_ADJUSTING,
      Command.INFO_WATER_BREWING,
    ]
  ) {
    return;
  }

  console.log(`====================`);
  console.log(`ID: ${msg.ID === 0x58 ? "X" : "Invalid"}`);
  console.log(
    `Direction: ${msg.direction === 0x2 ? "Machine to App" : "App to Machine"}`,
  );
  console.log(`Product: ${msg.product}`);
  console.log(`Command: ${msg.command.toString(16)}`);
  console.log(`Total Length: ${msg.total_length}`);

  let payload = "";
  for (let x = 0; x < msg.payload.byteLength; x++) {
    payload += " " + msg.payload.getUint8(x).toString(16);
  }
};
